import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { getDemoAccountDashboardPath, getDemoAccountUserType } from '../utils/demoAccounts';

const DashboardRedirect = ({ user, userData, loading, refreshUserData }) => {
  const [retryCount, setRetryCount] = useState(0);

  const fallbackUserType = userData?.userType || getDemoAccountUserType(user?.email);
  const fallbackDashboardPath = getDemoAccountDashboardPath(user?.email);
  const effectiveUserType = userData?.userType || fallbackUserType;

  // If logged in but userType is missing, retry fetching userData (max 3 retries)
  useEffect(() => {
    if (!loading && user?.uid && !effectiveUserType && retryCount < 3 && refreshUserData) {
      const timer = setTimeout(() => {
        console.log(`🔄 DashboardRedirect: retrying userData fetch (attempt ${retryCount + 1})...`);
        refreshUserData(user.uid);
        setRetryCount((c) => c + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, user, effectiveUserType, retryCount, refreshUserData]);

  // Still loading initial auth state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" />;
  }

  // User data not yet available and still retrying — wait
  if (!effectiveUserType && retryCount < 3) {
    return <LoadingSpinner />;
  }

  // After 3 retries still no userType — redirect to login to force re-auth
  if (!effectiveUserType) {
    console.warn('⚠️ DashboardRedirect: userData unavailable after retries. Sending to /login.');
    return <Navigate to="/login" />;
  }

  if (!userData && fallbackDashboardPath) {
    return <Navigate to={fallbackDashboardPath} />;
  }

  // Redirect based on user type
  switch (effectiveUserType) {
    case 'jobseeker':
      return <Navigate to="/dashboard/jobseeker" />;
    case 'employer':
      return <Navigate to="/dashboard/employer" />;
    case 'superadmin':
      return <Navigate to="/superadmin/dashboard" />;
    case 'team':
      return <Navigate to="/team/dashboard" />;
    case 'agent':
      return <Navigate to="/agent/dashboard" />;
    case 'franchise':
      return <Navigate to="/franchise/dashboard" />;
    default:
      return <Navigate to="/" />;
  }
};

export default DashboardRedirect;
